import { useSuspenseQuery } from '@apollo/client';
import { Suspense, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { PageLayout } from '@xing-com/crate-people-directory-page-layout';
import { TopLevelPageLink } from '@xing-com/crate-people-directory-top-level-page-link';
import {
  UserMessage,
  UserMessageType,
} from '@xing-com/crate-people-directory-user-message';
import { IconArrowRight } from '@xing-com/icons';
import { Row, Grid } from '@xing-com/xing-grid';

import { SEO_URL_META_DATA } from './constants';
import { PeopleStartPageDocument } from './graphql/start-page-query.gql-types';
import {
  ContentSpacer,
  StyledTextButton,
  StyledHeadline,
} from './start-page.styles';

type BasicPageLayoutProps = {
  seoTitle: string;
  seoDescription: string;
  index: boolean;
  seoUrl: string;
  pageName: string;
  hideLetterNavigation: boolean;
};

const StartPageInner: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  const { formatMessage } = useIntl();
  const { isLoggedOut } = useLoginState();

  const { data, error } = useSuspenseQuery(PeopleStartPageDocument, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (!isLoggedOut) {
      window.location.assign('https://www.xing.com/search/members');
    }
  }, [isLoggedOut]);

  if (!isLoggedOut) return;
  if (error || !data) throw error;

  const { letterTopLevelPages, cityTopLevelPages, seeMore } =
    data.peopleStartPage || {};

  if (!data.peopleStartPage || (!letterTopLevelPages && !cityTopLevelPages)) {
    return <EmptyStateFallback {...basicPageLayoutProps} />;
  }

  const sanitizedCityTopLevelPages = cityTopLevelPages?.map(
    (cityTopLevelPage) => {
      const cityUrl = cityTopLevelPage?.url ? `${cityTopLevelPage.url}` : '';
      return {
        cityName: cityTopLevelPage?.cityName || '',
        url: cityUrl,
        exampleNames: cityTopLevelPage?.exampleNames?.map((exampleName) => {
          const nameUrl = exampleName?.url ? `${exampleName.url}` : '';

          return {
            url: nameUrl,
            name: exampleName?.name || '',
          };
        }),
      };
    }
  );

  const sanitizedLetterTopLevelPages = letterTopLevelPages?.map(
    (letterTopLevelPage) => {
      const letterUrl = letterTopLevelPage?.url
        ? `${letterTopLevelPage.url}`
        : '';

      return {
        letter: letterTopLevelPage?.letter || '',
        url: letterUrl,
        exampleNames: letterTopLevelPage?.exampleNames?.map((exampleName) => {
          const nameUrl = exampleName?.url ? `${exampleName.url}` : '';

          return {
            url: nameUrl,
            name: exampleName?.name || '',
          };
        }),
      };
    }
  );

  return (
    <PageLayout {...basicPageLayoutProps}>
      <ContentSpacer $isLoading={false}>
        <StyledHeadline size="xxlarge">
          {formatMessage({ id: 'PD_PEOPLE_BY_LAST_NAME_HEADLINE' })}
        </StyledHeadline>
        <Grid>
          <Row>
            {sanitizedLetterTopLevelPages &&
              sanitizedLetterTopLevelPages.map(
                ({ letter, url, exampleNames }, index) => (
                  <TopLevelPageLink
                    key={`${letter}-${index}`}
                    title={letter}
                    url={url}
                    exampleNames={exampleNames}
                  />
                )
              )}
          </Row>
        </Grid>
      </ContentSpacer>
      {cityTopLevelPages && cityTopLevelPages.length ? (
        <ContentSpacer $isLoading={false}>
          <StyledHeadline size="xxlarge">
            {formatMessage(
              {
                id: 'PD_CITIES_PEOPLE_BY_CITY_HEADLINE',
              },
              { count: cityTopLevelPages.length }
            )}
          </StyledHeadline>
          <Grid>
            <Row>
              {sanitizedCityTopLevelPages?.map(
                ({ cityName, url, exampleNames }, index) => (
                  <TopLevelPageLink
                    key={`${cityName}-${index}`}
                    title={cityName}
                    url={url}
                    exampleNames={exampleNames}
                  />
                )
              )}
            </Row>
          </Grid>
          {seeMore ? (
            <StyledTextButton
              iconAfter
              icon={IconArrowRight}
              size="large"
              href="/people/cities"
            >
              {formatMessage({ id: 'PD_CITIES_SEE_MORE_CITIES_LINK' })}
            </StyledTextButton>
          ) : null}
        </ContentSpacer>
      ) : null}
    </PageLayout>
  );
};

const LoadingStateFallback: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  const { formatMessage } = useIntl();

  return (
    <PageLayout {...basicPageLayoutProps}>
      <ContentSpacer $isLoading={true}>
        <StyledHeadline size="large">
          {formatMessage({ id: 'PD_PEOPLE_BY_LAST_NAME_HEADLINE' })}
        </StyledHeadline>
      </ContentSpacer>
    </PageLayout>
  );
};

const EmptyStateFallback: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  return (
    <PageLayout {...basicPageLayoutProps}>
      <UserMessage type={UserMessageType.empty} />
    </PageLayout>
  );
};

const ErrorFallback: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  return (
    <PageLayout {...basicPageLayoutProps}>
      <UserMessage type={UserMessageType.error} />
    </PageLayout>
  );
};

export const StartPage: React.FC = () => {
  const { formatMessage } = useIntl();
  const basicPageLayoutProps = {
    seoTitle: formatMessage({ id: 'PD_ROOT_TITLE' }),
    index: true,
    seoDescription: formatMessage({ id: 'PD_META_DESCRIPTION_LVL_0' }),
    seoUrl: SEO_URL_META_DATA,
    pageName: 'index',
    hideLetterNavigation: true,
  };

  return (
    <ErrorBoundary onError={() => <ErrorFallback {...basicPageLayoutProps} />}>
      <Suspense fallback={<LoadingStateFallback {...basicPageLayoutProps} />}>
        <StartPageInner {...basicPageLayoutProps} />
      </Suspense>
    </ErrorBoundary>
  );
};
